import React, { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ValueTypes } from "../../shared/types";
import { ModalHelpText, ModalTitle, StyledOption, StyledSelect } from "../../shared/styles";
import { ChangeValueContainer, Container, InputContainer, StyledBox, StyledCheckbox, StyledInput } from "./styles";
import { messages } from "./messages";
import { AriType } from "../index";

interface Props {
  valueType?: ValueTypes;
  applyTo: AriType;
  canChangeValueType: boolean;
  onChangeValueType: (valueType: ValueTypes) => void;
  onChangeOperator: (operator: string) => void;
  onChangeValue: (value: number | boolean) => void;
  errors: string[];
  isBlockingDay?: boolean;
}

const CHECKBOX_VALUES = [ValueTypes.closed, ValueTypes.closeToDeparture, ValueTypes.closeToArrival];

function getAvailableValueTypes(applyTo: AriType) {
  if (applyTo === AriType.EXTRAS) {
    return [ValueTypes.rate, ValueTypes.availability, ValueTypes.closed];
  }
  if (applyTo === AriType.PROMOS) {
    return [ValueTypes.closed];
  }
  if (applyTo === AriType.COMPLEMENTS) {
    return [ValueTypes.rate];
  }
  return Object.keys(ValueTypes) as (keyof typeof ValueTypes)[];
}

export const UpdateValue = (
  {
    valueType,
    applyTo,
    canChangeValueType,
    onChangeValueType,
    onChangeOperator,
    onChangeValue,
    errors,
    isBlockingDay
  }: Props
): JSX.Element => {
  const intl = useIntl();
  const defaultValueType = useMemo(
    () => applyTo === AriType.PROMOS ? ValueTypes.closed : ValueTypes.rate,
    [applyTo]
  );

  const isCheckbox = useMemo(() => CHECKBOX_VALUES.includes(valueType || defaultValueType),
    [valueType, defaultValueType]
  );

  const availableValueTypes = useMemo(() => {
    return getAvailableValueTypes(applyTo)
      .map(value =>
        <StyledOption key={value} value={value}>
          {intl.formatMessage(messages[value])}
        </StyledOption>
      );
  }, [intl, applyTo]);

  const handleOnChangeValue = useCallback(value => {
    const finalValue = isCheckbox ? value : parseFloat(value);
    onChangeValue(finalValue);
  }, [isCheckbox, onChangeValue]);

  return (
    <Container>
      {isBlockingDay && (
        <ModalHelpText>
          <FormattedMessage {...messages.helpText} />
        </ModalHelpText>
      )}
      <ModalTitle>
        <FormattedMessage {...messages.change} />
      </ModalTitle>

      <ChangeValueContainer>
        {!canChangeValueType && valueType &&
          <StyledBox>
            <FormattedMessage {...messages[valueType]} />
          </StyledBox>
        }

        {canChangeValueType &&
          <div>
            <StyledSelect
              aria-label="value-type"
              value={valueType || defaultValueType}
              onChange={event => onChangeValueType(ValueTypes[event?.target?.value as keyof typeof ValueTypes])}
            >
              {availableValueTypes}
            </StyledSelect>
          </div>
        }

        <div>
          {!isCheckbox && (
            <StyledSelect aria-label="operator" onChange={event => onChangeOperator(event?.target?.value)}>
              <StyledOption value="=">
                {intl.formatMessage(messages.setValue)}
              </StyledOption>
              <StyledOption value="+">
                {intl.formatMessage(messages.increaseUnits)}
              </StyledOption>
              <StyledOption value="-">
                {intl.formatMessage(messages.decreaseUnits)}
              </StyledOption>
              {/** TODO: add increase and decrease percentage */}
            </StyledSelect>
          )}
        </div>

        <InputContainer>
          {isCheckbox && (
            <StyledCheckbox
              type="checkbox"
              placeholder={intl.formatMessage(messages.introduceValue)}
              onChange={event => handleOnChangeValue(event?.target?.checked)}
            />
          )}

          {!isCheckbox && (
            <StyledInput
              type="text"
              placeholder={intl.formatMessage(messages.introduceValue)}
              onChange={event => handleOnChangeValue(event?.target?.value)}
              error={errors.includes("value")}
            />
          )}
        </InputContainer>
      </ChangeValueContainer>
    </Container>
  );
};
